// VectorShift Letter JSON Format - simplified structure from VectorShift pipeline
export interface VectorShiftLetterData {
  Name: string;
  Phone: string;
  Email: string;
  City: string;
  State: string;
  LinkedIn: string;
  Body: string;
}

// Convert VectorShift letter format to LetterData for existing templates
export function convertVectorShiftToLetterData(
  vsLetter: VectorShiftLetterData, 
  companyName?: string, 
  jobTitle?: string
): LetterData {
  // Parse Body into paragraphs (split by double newlines or single newlines with blank lines)
  const bodyParagraphs = vsLetter.Body
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean);
  
  return {
    senderName: vsLetter.Name,
    senderPhone: vsLetter.Phone,
    senderEmail: vsLetter.Email,
    senderCity: vsLetter.City,
    senderState: vsLetter.State,
    senderLinkedIn: vsLetter.LinkedIn,
    recipientCompany: companyName,
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    salutation: 'Dear Hiring Manager,',
    bodyParagraphs,
    closing: 'Sincerely,',
    signature: vsLetter.Name
  };
}

// Letter data interface for all letter templates
export interface LetterData {
  senderName: string;
  senderPhone?: string;
  senderEmail?: string;
  senderCity?: string;
  senderState?: string;
  senderLinkedIn?: string;
  recipientName?: string;
  recipientTitle?: string;
  recipientCompany?: string;
  recipientAddress?: string;
  date?: string;
  subject?: string;
  salutation?: string;
  bodyParagraphs: string[];
  closing?: string;
  signature?: string;
}

// Executive Bio data interface
export interface ExecutiveBioData {
  name: string;
  title?: string;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  linkedIn?: string;
  bioParagraphs: string[];
  keyAccomplishments?: string[];
  expertise?: string[];
}

// Helper function to parse letter content into structured data
export function parseLetterContent(content: string): LetterData {
  const lines = content.split('\n').filter(line => line.trim());
  
  // Try to extract sender info from first few lines
  let senderName = '';
  let senderPhone = '';
  let senderEmail = '';
  let senderCity = '';
  let senderState = '';
  let senderLinkedIn = '';
  let recipientName = '';
  let recipientTitle = '';
  let recipientCompany = '';
  let date = '';
  let salutation = '';
  let closing = 'Sincerely,';
  let signature = '';
  const bodyParagraphs: string[] = [];
  
  let parsingState: 'header' | 'recipient' | 'body' | 'closing' = 'header';
  let currentParagraph = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Look for email pattern
    if (line.includes('@') && !senderEmail) {
      const emailMatch = line.match(/[\w.-]+@[\w.-]+\.\w+/);
      if (emailMatch) senderEmail = emailMatch[0];
    }
    
    // Look for phone pattern
    if (line.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/) && !senderPhone) {
      const phoneMatch = line.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/);
      if (phoneMatch) senderPhone = phoneMatch[0];
    }
    
    // Look for LinkedIn
    if (line.toLowerCase().includes('linkedin') && !senderLinkedIn) {
      senderLinkedIn = line;
    }
    
    // Look for date patterns
    if (line.match(/\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/i) && !date) {
      date = line;
      parsingState = 'recipient';
      continue;
    }
    
    // Look for salutation
    if (line.match(/^(Dear|Hello|Hi|Greetings)/i)) {
      salutation = line;
      parsingState = 'body';
      continue;
    }
    
    // Look for closing
    if (line.match(/^(Sincerely|Best regards|Kind regards|Regards|Warmly|Thank you|Best)/i) && parsingState === 'body') {
      closing = line.replace(/,?\s*$/, ',');
      parsingState = 'closing';
      continue;
    }
    
    // Parse based on state
    if (parsingState === 'header' && i === 0 && !line.includes('@') && !line.match(/\d{3}/)) {
      senderName = line;
    } else if (parsingState === 'recipient' && !salutation) {
      if (!recipientName && line.length < 50) {
        recipientName = line;
      } else if (!recipientTitle && line.length < 50) {
        recipientTitle = line;
      } else if (!recipientCompany && line.length < 50) {
        recipientCompany = line;
      }
    } else if (parsingState === 'body') {
      if (currentParagraph) {
        currentParagraph += ' ' + line;
      } else {
        currentParagraph = line;
      }
      
      // Check if next line is empty or different paragraph
      if (i + 1 >= lines.length || lines[i + 1].trim() === '' || lines[i + 1].trim().match(/^(Dear|Sincerely|Best|Thank)/i)) {
        if (currentParagraph.trim()) {
          bodyParagraphs.push(currentParagraph.trim());
        }
        currentParagraph = '';
      }
    } else if (parsingState === 'closing') {
      if (!signature && line) {
        signature = line;
      }
    }
  }
  
  // If no name found, use a default
  if (!senderName) senderName = signature || 'Your Name';
  if (!signature) signature = senderName;
  
  // If no body paragraphs, use the entire content
  if (bodyParagraphs.length === 0) {
    bodyParagraphs.push(content);
  }
  
  return {
    senderName,
    senderPhone,
    senderEmail,
    senderCity,
    senderState,
    senderLinkedIn,
    recipientName,
    recipientTitle,
    recipientCompany,
    date: date || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    salutation: salutation || 'Dear Hiring Manager,',
    bodyParagraphs,
    closing,
    signature
  };
}

// Helper function to parse executive bio content
export function parseExecutiveBioContent(content: string): ExecutiveBioData {
  const lines = content.split('\n').filter(line => line.trim());
  
  let name = '';
  let title = '';
  let phone = '';
  let email = '';
  let city = '';
  let state = '';
  let linkedIn = '';
  const bioParagraphs: string[] = [];
  const keyAccomplishments: string[] = [];
  const expertise: string[] = [];
  
  let currentParagraph = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // First non-empty line is usually the name
    if (!name && line && !line.includes('@') && !line.match(/\d{3}/)) {
      name = line;
      continue;
    }
    
    // Look for email
    if (line.includes('@') && !email) {
      const emailMatch = line.match(/[\w.-]+@[\w.-]+\.\w+/);
      if (emailMatch) email = emailMatch[0];
      continue;
    }
    
    // Look for phone
    if (line.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/) && !phone) {
      const phoneMatch = line.match(/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/);
      if (phoneMatch) phone = phoneMatch[0];
      continue;
    }
    
    // Look for LinkedIn
    if (line.toLowerCase().includes('linkedin') && !linkedIn) {
      linkedIn = line;
      continue;
    }
    
    // Check for accomplishment bullets
    if (line.match(/^[•✓\-\*]\s/) || line.match(/^\d+\.\s/)) {
      keyAccomplishments.push(line.replace(/^[•✓\-\*\d.]\s*/, ''));
      continue;
    }
    
    // Otherwise it's a bio paragraph
    if (currentParagraph) {
      currentParagraph += ' ' + line;
    } else {
      currentParagraph = line;
    }
    
    // Check for paragraph break
    if (i + 1 >= lines.length || lines[i + 1].trim() === '') {
      if (currentParagraph.trim()) {
        bioParagraphs.push(currentParagraph.trim());
      }
      currentParagraph = '';
    }
  }
  
  // If no paragraphs, use entire content
  if (bioParagraphs.length === 0) {
    bioParagraphs.push(content);
  }
  
  return {
    name: name || 'Executive Name',
    title,
    phone,
    email,
    city,
    state,
    linkedIn,
    bioParagraphs,
    keyAccomplishments,
    expertise
  };
}
